import React, { useState, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { IBasicStyledProps } from '@notino/react-styleguide-v20';

import { TrackingContextProvider, useTrackingContext } from '@context/TrackingContext';
import { trackClickGiftSectionEvent } from '@services/ecommerceService';
import { CartLayout } from '@sharedComponents/Layout';
import { GridDivider } from '@sharedComponents/Separators';
import { ReactFCV } from '@types';
import { DIVIDER_COLOR, DIVIDER_HEIGHT } from '@utils/constants';
import { CypressTestNamespace } from '@utils/test';

import { useCartPageContext } from '../../context';

import { BodyColumn, IconColumn, InfoColumn } from './componentHelpers';
import { GiftsList } from './GiftsList';
import { StyledCollapsibleContentHandler } from './styled';
import { getActionListTitle } from './utils/getActionListTitle';
import { shouldExpandCampaignsInCart } from './utils/shouldExpandCampaignsInCart';

export const Gifts: ReactFCV<IBasicStyledProps> = ({ ...rest }) => {
  const {
    state: { gifts },
    settings: { showGiftSelectionByDefault },
  } = useCartPageContext();

  const { getTimeFromInit } = useTrackingContext();
  const lastPageViewMs = React.useRef(0);
  TrackingContextProvider.useSubscribeToInitTimeReset(() => {
    lastPageViewMs.current = 0;
  });

  const { formatMessage } = useIntl();
  const getGiftActionTitle = getActionListTitle();
  const [showGifts, setShowGifts] = useState<boolean>(showGiftSelectionByDefault || shouldExpandCampaignsInCart(gifts));

  const collapsibleOnChange = useCallback(() => {
    trackClickGiftSectionEvent(showGifts, getTimeFromInit() - lastPageViewMs.current);
    setShowGifts((prevState) => !prevState);
  }, [showGifts, getTimeFromInit]);

  if (!gifts.length) {
    return null;
  }

  const infoComponent = (
    <StyledCollapsibleContentHandler>
      {gifts.map((campaign) => (
        <GiftsList key={campaign.id} title={formatMessage(getGiftActionTitle(campaign)!)} campaign={campaign} />
      ))}
    </StyledCollapsibleContentHandler>
  );

  return (
    <div {...rest}>
      <GridDivider color={DIVIDER_COLOR} height={DIVIDER_HEIGHT} />
      <CartLayout
        serviceActive={true}
        serviceDisabled={false}
        iconColumn={IconColumn(collapsibleOnChange)}
        bodyColumn={BodyColumn(showGifts, collapsibleOnChange)}
        infoColumn={InfoColumn(showGifts, infoComponent)}
        className={CypressTestNamespace.ECartPageGifts.GIFT_COLLAPSIBLE_SECTION}
      />
    </div>
  );
};
